import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, BadgeCheck, Clock, MapPin, Phone, Mail } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { Reveal, Overline } from "@/components/Reveal";
import { SITE, DLC } from "@/data/content";

export default function About() {
  return (
    <div className="bg-white" data-testid="about-page">
      <SEO
        title="About — Amaya Pro Network Lighting Control"
        description="Amaya Pro is a complete Network Lighting Control ecosystem — Bluetooth Mesh controls with DLC QPL listings and a 10-year in-house warranty."
        path="/about"
      />
      <PageHero
        dark
        overline="About Amaya"
        title="A modern Network Lighting Control ecosystem."
        sub="Amaya Pro exists to make commercial and industrial lighting connected, intelligent, secure and manageable — without the complexity."
      />

      <section className="border-t border-zinc-200 py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <Reveal>
            <Overline>Why Amaya exists</Overline>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight leading-tight">
              Lighting is the largest connected system in a building. It should act like it.
            </h2>
            <p className="text-zinc-500 mt-6 leading-relaxed">
              Networked lighting controls use connected sensors and fixtures to automatically adjust light for efficiency and convenience. Amaya Pro brings that capability together as one ecosystem: luminaires, sensors, controls, a Bluetooth Mesh network and the software to manage it all.
            </p>
            <p className="text-zinc-500 mt-4 leading-relaxed">
              The result is automated operation, centralized management, energy optimization and real-time monitoring — across residential, commercial and industrial facilities.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="space-y-4">
              {[
                { icon: BadgeCheck, t: "DLC QPL Listed", d: `Networked Lighting Controls listed with the DesignLights Consortium — Indoor (${DLC[0].productId}) and Outdoor (${DLC[1].productId}).` },
                { icon: ShieldCheck, t: "PSA Level 3 Security", d: "Device-level security specified across the Amaya Pro product family." },
                { icon: Clock, t: "10-Year In-House Warranty", d: "Backed by quick support and replacements to keep sites running without downtime." },
              ].map((c) => (
                <div key={c.t} className="border border-zinc-200 bg-white p-6 flex items-start gap-4" data-testid={`about-${c.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <c.icon size={22} className="text-signal shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-display text-lg font-bold">{c.t}</h3>
                    <p className="text-sm text-zinc-500 mt-1.5 leading-relaxed">{c.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-bone text-ink py-24" data-testid="about-company">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <Reveal>
            <Overline dark={false}>The company</Overline>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight leading-tight">
              The team behind Amaya Pro
            </h2>
            <p className="text-zinc-600 mt-6 leading-relaxed">
              Amaya Pro is a Network Lighting Control system listed on the DesignLights Consortium QPL — Indoor (Product ID N-Q0TFLJ) and Outdoor (Product ID N-KGDT1F).
            </p>
            <div className="mt-8 bg-white border border-dashed border-zinc-300 p-6" data-testid="about-placeholder">
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 mb-2">Content placeholder</p>
              <p className="text-sm text-zinc-500 leading-relaxed">
                Company history, leadership and partnership details will be published here once verified content is supplied by the company.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="bg-forest text-white p-8 md:p-10 h-full flex flex-col justify-between">
              <div>
                <h3 className="font-display text-2xl font-bold tracking-tight">Talk to the team</h3>
                <ul className="mt-8 space-y-5 text-sm">
                  <li className="flex items-center gap-3"><Phone size={16} className="text-signal" /> <a href={`tel:${SITE.phone}`} className="hover:text-signal transition-colors">{SITE.phone}</a></li>
                  <li className="flex items-center gap-3"><Mail size={16} className="text-signal" /> <a href={`mailto:${SITE.email}`} className="hover:text-signal transition-colors">{SITE.email}</a></li>
                  <li className="flex items-start gap-3"><MapPin size={16} className="text-signal mt-0.5" /> <span className="text-white/70">{SITE.address}</span></li>
                </ul>
                <p className="text-xs text-white/50 mt-6">Technical support: {SITE.supportPhone}</p>
              </div>
              <Link to="/contact" className="inline-flex items-center gap-2 mt-10 px-6 py-3 bg-signal text-white text-sm font-medium hover:bg-[#5c8f23] transition-colors w-max" data-testid="about-contact-btn">
                Contact Us <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
